import {
  ResourceType,
  type PermissionTreeItem,
  type PermissionTreeOrigin
} from '@/Network/Types/Responses/PermissionResponses'
import { getRelationLabel } from '@/constants/authorizationLabels'

/**
 * Chave única de um nó visual da árvore (expandir/colapsar). A árvore nova tem
 * UM nó por recurso, então `tipo-id` basta; a parentKey encadeada desambigua o
 * mesmo recurso aparecendo sob pais diferentes.
 */
export const getNodeKey = (node: PermissionTreeItem, parentKey?: string): string => {
  const ownKey = `${node.resource_type}-${node.resource_id}`
  return parentKey ? `${parentKey}>${ownKey}` : ownKey
}

/** Nome curto da origem: "Direta" ou "Grupo {nome}" (fallback "Grupo"). */
export const getOriginName = (origin: PermissionTreeOrigin): string => {
  if (origin.kind === 'direct') return 'Direta'
  return origin.group_name ? `Grupo ${origin.group_name}` : 'Grupo'
}

/**
 * Texto de tooltip de UMA origem: `{origem} — {relation em pt-BR}`, com o
 * sufixo `(herdada)` quando a permissão desce de um recurso pai.
 */
export const getOriginTooltip = (origin: PermissionTreeOrigin): string => {
  const suffix = origin.is_inherited ? ' (herdada)' : ''
  return `${getOriginName(origin)} — ${getRelationLabel(origin.relation)}${suffix}`
}

/** Escala é o último nível da árvore: não expande nem tem filhos. */
export const isLeafNode = (node: PermissionTreeItem): boolean =>
  node.resource_type === ResourceType.Schedule

/**
 * Coleta recursivamente as chaves de todos os nós expandíveis (não-folha) da
 * árvore, encadeando a parentKey — mesmo formato usado na renderização. Usado
 * pelo "Expandir tudo"; folhas (escalas) ficam de fora.
 */
export const collectExpandableKeys = (
  nodes: PermissionTreeItem[],
  parentKey?: string
): string[] => {
  const keys: string[] = []
  for (const node of nodes) {
    if (isLeafNode(node)) continue
    const key = getNodeKey(node, parentKey)
    keys.push(key)
    if (node.children?.length) keys.push(...collectExpandableKeys(node.children, key))
  }
  return keys
}
